import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getBrandingConfig } from 'services/brandingService';

const BrandingContext = createContext({});

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const config = await getBrandingConfig();
        setBranding(config);

        // Update document metadata
        updateDocumentMetadata(config);
      } catch (error) {
        console.error('Failed to load branding:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  const updateDocumentMetadata = (config) => {
    // Update page title
    if (config.meta?.title) {
      document.title = config.meta.title;
    }

    // Update favicon
    if (config.assets?.favicon_path) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const baseUrl = apiUrl.replace('/api', '');

      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = `${baseUrl}${config.assets.favicon_path}`;
    }

    // Update apple touch icon
    if (config.assets?.apple_touch_icon) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const baseUrl = apiUrl.replace('/api', '');

      let appleLink = document.querySelector("link[rel='apple-touch-icon']");
      if (!appleLink) {
        appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        document.head.appendChild(appleLink);
      }
      appleLink.href = `${baseUrl}${config.assets.apple_touch_icon}`;
    }

    // Update meta theme color
    if (config.theme?.meta_theme_color) {
      let metaTheme = document.querySelector("meta[name='theme-color']");
      if (!metaTheme) {
        metaTheme = document.createElement('meta');
        metaTheme.name = 'theme-color';
        document.head.appendChild(metaTheme);
      }
      metaTheme.content = config.theme.meta_theme_color;
    }

    // Update meta description
    if (config.meta?.description) {
      let metaDesc = document.querySelector("meta[name='description']");
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = config.meta.description;
    }
  };

  const getAssetUrl = (assetPath) => {
    if (!assetPath) return '';

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
    const baseUrl = apiUrl.replace('/api', '');

    return `${baseUrl}${assetPath}`;
  };

  const value = {
    branding,
    loading,
    getAssetUrl
  };

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

BrandingProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default BrandingContext;
